import { Lightning as lng, Log, Registry, Router } from '@lightningjs/sdk';
import { Theme } from '../config/theme';
import { Spinner } from '../components/shared/Spinner';
import { TitleBar } from '../components/player/TitleBar';
import { ErrorMessage } from '../components/player/ErrorMessage';
import { Controls } from '../components/player/Controls';
import { TrickPlayOverlay } from '../components/player/TrickPlayOverlay';
import { ZappingBar } from '../components/player/ZappingBar';
import { LinearStreamInfo } from '../Business/player/streamInfo/LinearStreamInfo';
import { EpgUtil } from '../Business/utility/EpgUtil';
import { hasReplayRights } from '../Business/detail/epg/EpgWatchability';
import { StaticPageUrls } from '../api/analytics/ClientEvents';
import { MiniEpg } from '../components/player/miniEpg/MiniEpg';
import { ChannelManager } from '../api/ChannelManager';
import { EpgApiV5 } from '../api/EpgApiV5';
import ChannelNumberDisplay from '../components/shared/ChannelNumberDisplay';

const _constants = {
  ChannelNumberInput: {
    marginHorizontal: 80,
    marginVertical: 80,
  },
};

export class PlayerPage extends lng.Component {
  static _template() {
    return {
      color: Theme.Colors.Transparent,
      w: w => w,
      h: h => h,
      Spinner: {
        type: Spinner,
        x: w => w * 0.5,
        y: h => h * 0.5,
        mount: 0.5,
        visible: false,
      },
      TitleBar: {
        type: TitleBar,
        x: 0,
        y: 0,
        visible: false,
      },
      ChannelNumberDisplay: {
        mountX: 1,
        x: w => w - _constants.ChannelNumberInput.marginHorizontal,
        y: _constants.ChannelNumberInput.marginVertical,
        type: ChannelNumberDisplay,
        signals: {
          channelNumberTriggered: true,
        },
      },
      ErrorOverlay: {
        w: w => w,
        h: h => h,
        type: ErrorMessage,
      },
      ZappingBar: {
        x: 0,
        y: 0,
        type: ZappingBar,
        alpha: 0,
      },
      MiniEpg: {
        x: 0,
        y: 1080,
        mountY: 1,
        w: w => w,
        h: 380,
        type: MiniEpg,
        alpha: 0,
      },
      Controls: {
        type: Controls,
        x: 0,
        y: 840,
        visible: false,
      },
      TrickPlayOverlay: {
        type: TrickPlayOverlay,
        x: w => w * 0.5,
        y: h => h * 0.5,
        mount: 0.5,
        visible: false,
      },
    };
  }

  /**
   * Implements states described in {@link OverlayState}
   */
  static _states() {
    return [
      class Hidden extends this {
        _getFocused() {
          return this;
        }
      },
      class Title extends this {
        $enter() {
          this.tag('TitleBar').visible = true;
        }

        $exit() {
          this.tag('TitleBar').visible = false;
        }

        _getFocused() {
          return this;
        }
      },
      class Controls extends this {
        $enter() {
          if (this._uiStateConfig && this._uiStateConfig.focusSeekbar) {
            this.tag('Controls').focusSeekbar();
          }
          this.tag('TitleBar').visible = true;
          this.tag('Controls').visible = true;
        }

        $exit() {
          this.tag('TitleBar').visible = false;
          this.tag('Controls').visible = false;
        }

        _getFocused() {
          return this.tag('Controls');
        }
      },
      class TrickPlay extends this {
        $enter() {
          this.tag('TitleBar').visible = true;
          this.tag('Controls').visible = true;
          this.tag('TrickPlayOverlay').visible = true;
        }

        $exit() {
          this.tag('TitleBar').visible = false;
          this.tag('Controls').visible = false;
          this.tag('TrickPlayOverlay').visible = false;
        }

        _getFocused() {
          return this.tag('Controls');
        }
      },
      class ChannelBar extends this {
        $enter() {
          this.tag('ZappingBar').params = this._uiStateConfig;
          this.tag('ZappingBar').setSmooth('alpha', 1, { duration: 1 });
        }

        $exit() {
          this.tag('ZappingBar').setSmooth('alpha', 0);
        }

        _getFocused() {
          return this.tag('ZappingBar');
        }
      },
      class MiniEpg extends this {
        $enter() {
          // this.tag('MiniEpg').params = this._uiStateConfig;
          this.tag('MiniEpg').setSmooth('alpha', 1, { duration: 1 });
          this.tag('TitleBar').visible = true;
        }

        $exit() {
          this.tag('MiniEpg').setSmooth('alpha', 0);
          this.tag('TitleBar').visible = false;
        }

        _getFocused() {
          return this.tag('MiniEpg');
        }
      },
      class Error extends this {
        _getFocused() {
          return this.tag('ErrorOverlay');
        }
      },
      // TODO ChannelBar
      // TODO MiniEpg
    ];
  }

  _onChanged() {
    Log.info('[PlayerPage]: _init', this, this.backend);
    this.backend?.logService.trackPageView(StaticPageUrls.videoPlayer);
    ChannelManager.validChannels(this.backend, false).then(result => {
      this.tag('ZappingBar').backend = this.backend;
      this.tag('ZappingBar').componentOwner = this;
      this.tag('ZappingBar').catalogID = this.backend.deviceProfile.profile.catalog;
      this.tag('ZappingBar').channels = result.validChannels;
    });

    this.tag('MiniEpg').backend = this.backend;

    this._playerController = this.backend.playerController;
    this._playerController.uiEventHandlers.spinnerHandler = enabled => (this.tag('Spinner').visible = enabled);
    this.tag('TitleBar').registerEventHandler(this._playerController);
    this.tag('ErrorOverlay').registerEventHandler(this._playerController);
    this.tag('TrickPlayOverlay').registerEventHandler(this._playerController);
    this._overlayHelper = this._playerController.overlayHelper;
    this._overlayHelper.overlayStateHandler = this.changeOverlayState.bind(this);
    if (this.params.streamInfo) {
      this._playerController.changeStream(this.params.streamInfo, undefined, undefined, this.params.resetAutoplay);
    } else {
      if (!this._playerController.isStreaming) {
        this._playerController.fallbackToLinearStream();
      }
    }
    this.tag('Controls').registerEventHandler(this._playerController, this._playerController.seekEnabled);
    this._playerController.onUiAttached();
    const playerModalShown = this.widgets.playermodal.isVisible;
    const dialogShown = this.widgets.dialog.isVisible;
    if (playerModalShown || dialogShown) {
      // dont focus page, keep widget focussed
    } else {
      Router.focusPage();
    }
  }

  $openPlayerModal(page, params) {
    this.widgets.playermodal.$openModal(page, params);
  }

  closePlayerModals() {
    this.widgets.playermodal.clearModals();
  }

  _inactive() {
    this._playerController.removeUIHandlers();
  }

  _handleEnter() {
    this._overlayHelper.handleEnter();
  }

  _handleUp() {
    this._overlayHelper.handleUp();
  }

  _handleLeft() {
    this._overlayHelper.handleLeft();
  }

  _handleRight() {
    this._overlayHelper.handleRight();
  }

  _handleDown() {
    this._overlayHelper.handleDown();
  }

  _handleBack() {
    const doExit = this._overlayHelper.handleBack();
    if (doExit) {
      this.fireAncestors('$verifyCloseApp');
    }
  }

  _handlePlayPause() {
    this._overlayHelper.handlePlayPause();
  }

  _handlePlay() {
    this._overlayHelper.handlePlay();
  }

  _handlePause() {
    this._overlayHelper.handlePause();
  }

  _handleStop() {
    this._overlayHelper.handleStop();
  }

  _handleRewind() {
    this._overlayHelper.handleRewind();
  }

  _handleForward() {
    this._overlayHelper.handleForward();
  }

  _handleChannelUp() {
    if (!(this.backend.playerController.streamInfo instanceof LinearStreamInfo)) return;
    this._overlayHelper.handleChannelUp();
  }

  _handleChannelDown() {
    if (!(this.backend.playerController.streamInfo instanceof LinearStreamInfo)) return;
    this._overlayHelper.handleChannelDown();
  }

  _handleNumberInput(e) {
    if (!(this.backend.playerController.streamInfo instanceof LinearStreamInfo)) return;
    this.tag('ChannelNumberDisplay').numberEvent(e);
  }

  channelNumberTriggered(chanNum) {
    this.backend.playerController.playChannelNumber(chanNum);
  }

  /**
   * @param {OverlayState} state
   * @param {UIStateConfig} [stateConfig]
   */
  changeOverlayState(state, stateConfig) {
    Log.info('change overlay state', state, stateConfig);
    this._uiStateConfig = stateConfig;
    this.uiState = state;
  }

  /**
   * @param {OverlayState} value
   */
  set uiState(value) {
    this._setState(value);
  }

  $changeStream(channel, updateUI = true) {
    let backend = this.backend;

    const fromUntil = EpgUtil.epgFromUntil(backend);

    EpgApiV5.channelLineup(backend, channel.channelId, fromUntil.from, fromUntil.until).then(schedules => {
      const currentSchedule = EpgUtil.findCurrentSchedule(schedules.schedules);
      if (!currentSchedule) {
        return;
      }

      let replay = hasReplayRights(currentSchedule, channel);

      /**
       * @type {Promise<IProgramDetailV5 | undefined>}
       */
      let getProgramPromise;
      if (currentSchedule) {
        getProgramPromise = EpgApiV5.program(backend, currentSchedule.programId);
      } else {
        getProgramPromise = Promise.resolve(undefined);
      }

      getProgramPromise.then(
        /** @type {IProgramDetailV5 | undefined} */ program => {
          /** @type {EpgEvent} */
          const epgEvent = {
            channel: channel,
            program: program?.program,
            series: program?.series,
            schedule: currentSchedule,
            recording: undefined,
          };

          let streamInfo = new LinearStreamInfo(epgEvent, backend, replay);
          this._playerController.changeStream(streamInfo, updateUI);

          this.tag('Controls').registerEventHandler(this._playerController, this._playerController.seekEnabled);
          this._overlayHelper = this._playerController.overlayHelper;
          this._overlayHelper.overlayStateHandler = this.changeOverlayState.bind(this);
        }
      );
    });
  }

  $updateControls() {
    this.tag('Controls').registerEventHandler(this._playerController, this._playerController.seekEnabled);
  }

  $stopOverlayTimeout() {
    this._overlayHelper.stopShowTimer();
  }

  $startOverlayTimeout() {
    this._overlayHelper.startShowTimer();
  }
}
